/**
 * CleverTap Error Handler
 * Handles all CleverTap-related errors silently and logs them for debugging
 */

class CleverTapErrorHandler {
  constructor() {
    this.errors = [];
    this.maxErrors = 100; // Keep last 100 errors
  }

  /**
   * Handle CleverTap errors silently
   * @param {Error} error - The error object
   * @param {string} context - Context where error occurred
   * @param {Object} additionalData - Additional data for debugging
   */
  handleError(error, context = 'Unknown', additionalData = {}) {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      context,
      message: error?.message || 'Unknown error',
      stack: error?.stack,
      additionalData,
      userAgent: navigator.userAgent,
    };

    // Add to errors array (keep only last maxErrors)
    this.errors.push(errorEntry);
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }

    // Could send to analytics or monitoring service here
    this.reportError(errorEntry);
  }

  /**
   * Report error to monitoring service (implement as needed)
   * @param {Object} errorEntry - The error entry object
   */
  reportError(errorEntry) {
    // Implementation for error reporting service
    // e.g., Sentry, LogRocket, or custom analytics
    try {
      // Example: Send to monitoring service
      // this.sendToMonitoringService(errorEntry);
    } catch (reportingError) {
      // Silent fail - don't let error reporting break the app
    }
  }

  /**
   * Get recent errors for debugging
   * @param {number} count - Number of recent errors to return
   * @returns {Array} Array of recent errors
   */
  getRecentErrors(count = 10) {
    return this.errors.slice(-count);
  }

  /**
   * Clear all stored errors
   */
  clearErrors() {
    this.errors = [];
  }

  /**
   * Get error statistics
   * @returns {Object} Error statistics
   */
  getErrorStats() {
    const now = new Date();
    const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const lastHour = new Date(now.getTime() - 60 * 60 * 1000);

    const recent24h = this.errors.filter(
      error => new Date(error.timestamp) > last24Hours
    );
    const recent1h = this.errors.filter(
      error => new Date(error.timestamp) > lastHour
    );

    return {
      total: this.errors.length,
      last24Hours: recent24h.length,
      lastHour: recent1h.length,
      contextBreakdown: this.getContextBreakdown(),
    };
  }

  /**
   * Get breakdown of errors by context
   * @returns {Object} Context breakdown
   */
  getContextBreakdown() {
    const breakdown = {};
    this.errors.forEach(error => {
      breakdown[error.context] = (breakdown[error.context] || 0) + 1;
    });
    return breakdown;
  }
}

// Create singleton instance
const clevertapErrorHandler = new CleverTapErrorHandler();

// Export the handleError function as a named export for convenience
export const handleError = (error, context, additionalData) => 
  clevertapErrorHandler.handleError(error, context, additionalData);

export default clevertapErrorHandler;
